import { Search } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import {
    Box,
    IconButton,
    InputAdornment,
    TextField,
    Toolbar,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { GridFilterModel, GridSearchIcon } from "@mui/x-data-grid-pro";
import { useDebouncedEffect } from "@react-hookz/web";
import { useEffect, useRef, useState } from "react";
import { ClauseChecklistsFilter } from "./ClauseChecklistsFilter";

interface IProps {
    /**
     * Callback function to handle filter model changes.
     * @param filterModel The filter model to apply.
     */
    handleFilterModelChange: (filterModel: GridFilterModel) => void;
}

/**
 * Displays the toolbar for the clause checklists list.
 */
export const ClauseChecklistsListToolbar = ({
    handleFilterModelChange,
}: IProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const inputRef = useRef<HTMLInputElement>(null);

    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        // On mobile, the quick filter is not expanded by default
        if (isMobile) {
            setExpanded(false);
        } else {
            setExpanded(true);
        }
    }, [isMobile]);

    useDebouncedEffect(
        () => {
            if (search) {
                handleFilterModelChange({
                    items: [],
                    quickFilterValues: search.split(" "),
                });
            } else {
                handleFilterModelChange({ items: [] });
            }
        },
        [search],
        250
    );

    return (
        <Toolbar disableGutters>
            {/* Filter */}
            <ClauseChecklistsFilter
                handleFilterModelChange={handleFilterModelChange}
            />

            <Box flexGrow={1} />

            {/* Search */}
            <div
                style={{
                    display: "grid",
                    alignItems: "center",
                    marginLeft: "auto",
                }}
            >
                <IconButton
                    onClick={() => {
                        setExpanded(true);
                        inputRef.current?.focus();
                    }}
                    sx={{
                        gridArea: "1 / 1",
                        width: "min-content",
                        height: "min-content",
                        zIndex: 1,
                        opacity: expanded ? 0 : 1,
                        pointerEvents: expanded ? "none" : "auto",
                        transition: theme.transitions.create(["opacity"]),
                    }}
                >
                    <GridSearchIcon fontSize="small" />
                </IconButton>
                <TextField
                    placeholder="Search..."
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onBlur={() => {
                        if (!search && isMobile) {
                            setExpanded(false);
                        }
                    }}
                    variant="standard"
                    sx={{
                        gridArea: "1 / 1",
                        overflowX: "clip",
                        width: expanded ? (isMobile ? 200 : 260) : 36,
                        opacity: expanded ? 1 : 0,
                        transition: theme.transitions.create([
                            "width",
                            "opacity",
                        ]),
                    }}
                    slotProps={{
                        input: {
                            inputRef: inputRef,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            setSearch("");
                                            inputRef.current?.focus();
                                        }}
                                        edge="end"
                                        size="small"
                                        aria-label="Clear search"
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                />
            </div>
        </Toolbar>
    );
};
